import { DecisionLine } from '../components/DecisionLine';
import { useEffect, useMemo, useState } from 'react';
import { addonsApi, progressApi, useAddons, type AddonMeta, type AddonVideo, type WatchCtx } from '../addons';
import { StreamList } from '../components/AddonSources';
import { useApp } from '../context';
import type { PlexItem } from '../plex';
import { goBack, linkProps } from '../router';
import { LocalListButton } from '../components/LocalListButton';

/** Titel uit een addon-catalogus: info, afleveringen en bronnen. Staat hij ook in Plex, dan kun je daarheen. */
export function AddonTitle({ addonId, type, id }: { addonId: string; type: string; id: string }) {
  const { plex } = useApp();
  const addons = useAddons();
  const addon = addons?.addons.find((a) => a.id === addonId);
  const [meta, setMeta] = useState<(AddonMeta & { videos: AddonVideo[] }) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [season, setSeason] = useState<number | null>(null);
  const [episode, setEpisode] = useState<AddonVideo | null>(null);
  const [inPlex, setInPlex] = useState<PlexItem | null>(null);
  // Kijkstand per aflevering (bekeken / half gekeken) en "verder kijken" vanuit Home (?ep=)
  const [prog, setProg] = useState<Record<string, { position: number; duration: number; watched: boolean }>>({});
  const wantEp = useMemo(() => new URLSearchParams(location.search).get('ep'), []);
  useEffect(() => {
    progressApi.of(id).then((r) => setProg(Object.fromEntries(r.items.map((x) => [x.id, x])))).catch(() => undefined);
  }, [id]);

  useEffect(() => {
    addonsApi.meta(addonId, type, id)
      .then((r) => {
        setMeta(r.meta);
        const seasons = [...new Set(r.meta.videos.map((v) => v.season ?? 0))].sort((a, b) => a - b);
        const want = wantEp ? r.meta.videos.find((v) => v.id === wantEp) : undefined;
        setSeason(want ? want.season ?? 0 : seasons.find((s) => s > 0) ?? seasons[0] ?? null);
        if (want) setEpisode(want);
      })
      .catch(() => setError('Deze titel kon niet geladen worden.'));
  }, [addonId, type, id]);

  // Eerst Plex: als de titel al in je bibliotheek staat
  useEffect(() => {
    const imdb = id.match(/^tt\d+/)?.[0];
    if (!imdb || (type !== 'movie' && type !== 'series')) return;
    plex.findByGuid(`imdb://${imdb}`, type === 'movie' ? 1 : 2).then(setInPlex).catch(() => undefined);
  }, [id, type]);

  const seasons = useMemo(() => [...new Set((meta?.videos ?? []).map((v) => v.season ?? 0))].sort((a, b) => a - b), [meta]);
  const episodes = (meta?.videos ?? []).filter((v) => (v.season ?? 0) === season).sort((a, b) => (a.episode ?? 0) - (b.episode ?? 0));
  const hasVideos = (meta?.videos.length ?? 0) > 0;
  const plexFirst = addons?.sourceOrder !== 'addons-first' && addons?.sourceOrder !== 'addons-only';

  if (error) return <main className="page"><p className="placeholder-row error" role="alert">{error}</p></main>;
  if (!meta) return <main className="page" aria-busy="true"><div className="row-skeleton" style={{ margin: '0 var(--gutter)' }} /></main>;

  const name = meta.name || 'Titel';
  const ctxFor = (v?: AddonVideo): WatchCtx => (v
    ? { id: v.id, type: 'series', metaId: meta.id, addonId, name, epTitle: v.title || undefined, season: v.season ?? undefined, episode: v.episode ?? undefined, poster: meta.poster ?? undefined, background: v.thumbnail ?? meta.background ?? undefined }
    : { id: meta.id, type: 'movie', metaId: meta.id, addonId, name, poster: meta.poster ?? undefined, background: meta.background ?? undefined });
  const pct = (vid: string) => {
    const p = prog[vid];
    return p && !p.watched && p.duration > 0 && p.position > 0 ? Math.min(1, p.position / p.duration) : null;
  };
  return (
    <main>
      <section className="hero hero-detail" style={meta.background ? { backgroundImage: `url("${meta.background}")` } : undefined}>
        <div className="hero-body">
          <button className="btn btn-sm saga-back" onClick={goBack}>‹ Terug</button>
          {addon && <div className="eyebrow">Uit {addon.name}</div>}
          {meta.logo ? <img className="saga-logo" src={meta.logo} alt={name} /> : <h1>{name}</h1>}
          <div className="badges">
            <span className="muted">{[meta.type === 'series' ? 'Serie' : meta.type === 'movie' ? 'Film' : meta.type, meta.releaseInfo, meta.imdbRating ? `★ ${meta.imdbRating}` : null, meta.genres.join(', ') || null].filter(Boolean).join(' · ')}</span>
          </div>
          <DecisionLine genres={meta.genres} />
          {meta.description && <p>{meta.description}</p>}
          {/^tt\d+/.test(meta.id) && (meta.type === 'movie' || meta.type === 'series') && (
            <div className="hero-actions" data-focus-group>
              <LocalListButton imdb={meta.id.match(/^tt\d+/)![0]} title={name} />
            </div>
          )}
          {inPlex && (
            <div className="hero-actions" data-focus-group>
              <a className={`btn${plexFirst ? ' btn-primary' : ''}`} data-autofocus {...linkProps(`/item/${inPlex.ratingKey}`)}>In je Plex-bibliotheek</a>
            </div>
          )}
        </div>
      </section>

      {hasVideos ? (
        <section className="row" aria-label="Afleveringen">
          <h2>Afleveringen</h2>
          {seasons.length > 1 && (
            <div className="filterbar" data-focus-group>
              {seasons.map((s) => (
                <button key={s} className="chip" aria-pressed={s === season} onClick={() => { setSeason(s); setEpisode(null); }}>{s === 0 ? 'Extra' : `Seizoen ${s}`}</button>
              ))}
            </div>
          )}
          <ul className="ad-episodes" data-focus-group>
            {episodes.map((v) => (
              <li key={v.id}>
                <button className="ad-episode" aria-pressed={episode?.id === v.id} onClick={() => setEpisode(v)}>
                  <span className="ad-ep-thumb" style={v.thumbnail ? { backgroundImage: `url("${v.thumbnail}")` } : undefined}>
                    {prog[v.id]?.watched && <span className="watched-mark" aria-label="Gezien">✓</span>}
                    {pct(v.id) !== null && <span className="progress"><span style={{ width: `${Math.round(pct(v.id)! * 100)}%` }} /></span>}
                  </span>
                  <span className="ad-ep-body">
                    <strong>{v.episode != null ? `${v.episode}. ` : ''}{v.title || `Aflevering ${v.episode ?? ''}`}</strong>
                    {v.released && <span className="subtle">{new Date(v.released).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                    {v.overview && <span className="subtle clamp-2">{v.overview}</span>}
                  </span>
                </button>
                {episode?.id === v.id && (
                  <div className="ad-ep-streams">
                    <StreamList type={meta.type} id={v.id} title={`${name} · ${v.title}`} ctx={meta.type === 'series' ? ctxFor(v) : undefined} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <section className="row ad-sources" aria-label="Bronnen">
          <h2>Bronnen</h2>
          <StreamList type={meta.type} id={meta.id} title={name} live={meta.type !== 'movie' && meta.type !== 'series'} ctx={meta.type === 'movie' ? ctxFor() : undefined} />
        </section>
      )}
      <div className="footer-space" />
    </main>
  );
}
